import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { formatDataRateSmart } from "kontrolEdge/helpers/formatDataRate";
import { OverviewSites } from "../Types";
import { MeterBar } from "./OverviewPanel";
import { HEALTH_DOT } from "./ServiceScopeChips";
import { ServiceHealth, ServiceScope, SiteSummary, sitesInScope } from "./overviewModel";
import { MapNode, TopologyNode, buildMapClouds, buildMapLinks } from "./mapModel";
import { DIAGRAM, DiagramLayout, layoutDiagram, orderSites } from "./diagramModel";
import { CloudMark, HEALTH_STROKE, PILL_BORDER, TopologyLegend, cloudPlateClass } from "./topologyParts";
import { skipTopologyIntro } from "./useTopologyIntro";

/** Width assumed before the first measure (and in jsdom, which has no ResizeObserver). */
const FALLBACK_WIDTH = 800;

export type SiteSelection = Pick<MapNode, "key" | "siteIds" | "label">;

/** A DCI arc out of the cards' left edge and back, bulging `depth` px left at its apex. */
function arcPath(layout: DiagramLayout, top: number, bottom: number, depth: number): string {
  const x = layout.cardLeft;
  return `M${x},${top} Q${x - 2 * depth},${(top + bottom) / 2} ${x},${bottom}`;
}

/** A cloud line: leaves the card level and arrives at the cloud level. */
function cloudPath(from: [number, number], to: [number, number]): string {
  const middle = (from[0] + to[0]) / 2;
  return `M${from[0]},${from[1]} C${middle},${from[1]} ${middle},${to[1]} ${to[0]},${to[1]}`;
}

function SiteCard({
  site,
  name,
  health,
  isSelected,
  onClick,
  style,
}: {
  site: SiteSummary;
  name: string;
  health: ServiceHealth;
  isSelected: boolean;
  onClick: () => void;
  style: React.CSSProperties;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isSelected}
      aria-label={`Show services at ${name}`}
      title={name === site.id ? name : `${name} (${site.id})`}
      // A card that stays slides to its new row; a card the scope adds fades in.
      className={`absolute flex flex-col justify-center gap-1.5 rounded-lg border bg-semantic-bg-primary px-3 text-left shadow-sm transition-[top,left,width,box-shadow] duration-500 ease-out hover:shadow-md motion-safe:animate-topology-in motion-reduce:transition-none ${
        isSelected ? "border-semantic-border-brand-primary ring-2 ring-semantic-border-brand-secondary" : PILL_BORDER[health]
      }`}
      style={style}
    >
      <span className="flex min-w-0 items-center gap-2">
        <span className={`h-2 w-2 shrink-0 rounded-full ${HEALTH_DOT[health]}`} aria-hidden />
        <span className="truncate font-heading text-sm font-bold text-semantic-text-primary">{name}</span>
      </span>
      <span className="flex items-baseline justify-between gap-2 text-[11px] text-semantic-text-secondary">
        <span className="shrink-0">
          {site.ports} {site.ports === 1 ? "port" : "ports"}
          {site.portsDown > 0 && <span className="font-semibold text-semantic-text-danger"> · {site.portsDown} down</span>}
        </span>
        <span className="truncate">
          <span className="font-semibold text-semantic-text-primary">{formatDataRateSmart(site.allocatedMbps)}</span> of{" "}
          {formatDataRateSmart(site.capacityMbps)}
        </span>
      </span>
      <MeterBar value={site.allocatedMbps} max={site.capacityMbps} heightClassName="h-1" />
    </button>
  );
}

/**
 * The scope's topology for sites with no location on file: the same cards,
 * DCIs and clouds as the map, laid out as a diagram (diagramModel). A card is
 * the map pin's equivalent — a click narrows the service list to that site.
 */
export function SitesDiagram({
  sites,
  locations,
  healthBySite,
  cloudHealthBySite,
  scope,
  selectedKey,
  onSelectNode,
}: {
  sites: SiteSummary[];
  /** Names, DCIs and clouds; null when the page reads the four endpoints. */
  locations: OverviewSites | null;
  healthBySite: Record<string, ServiceHealth>;
  cloudHealthBySite: Record<string, ServiceHealth>;
  scope: ServiceScope;
  selectedKey: string | null;
  onSelectNode: (site: SiteSelection | null) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(FALLBACK_WIDTH);
  useEffect(skipTopologyIntro, []);

  const nameById = useMemo(
    () => new Map((locations?.sites ?? []).map((location) => [location.datacenterId, location.displayName])),
    [locations]
  );
  // Without the overview endpoint there is no placement to scope by: every site shows.
  const scopedSites = useMemo(() => {
    if (!locations) return sites;
    const inScope = sitesInScope(scope, sites.map((site) => site.id), locations.placements ?? {});
    return sites.filter((site) => inScope.has(site.id));
  }, [sites, locations, scope]);

  const isEmpty = scopedSites.length === 0;
  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width > 0) setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
    // Re-attached when the drawing replaces the empty state, which has no ref.
  }, [isEmpty]);

  const showLinks = scope === "all" || scope === "datacenterInterconnect";
  const showClouds = scope === "all" || scope === "cloudConnectivity";
  const nodes: TopologyNode[] = useMemo(
    () => scopedSites.map((site) => ({ key: site.id, siteIds: [site.id] })),
    [scopedSites]
  );
  const links = useMemo(
    () => (showLinks && locations ? buildMapLinks(locations.links, nodes) : []),
    [showLinks, locations, nodes]
  );
  const clouds = useMemo(
    () => (showClouds && locations ? buildMapClouds(locations.cloudLinks ?? [], nodes, cloudHealthBySite) : []),
    [showClouds, locations, nodes, cloudHealthBySite]
  );
  const layout = useMemo(
    () =>
      layoutDiagram(
        orderSites(
          scopedSites.map((site) => site.id),
          links
        ),
        links,
        clouds,
        width
      ),
    [scopedSites, links, clouds, width]
  );

  if (isEmpty) {
    return (
      <p className="flex h-40 items-center justify-center rounded-lg border border-semantic-border-primary bg-semantic-bg-secondary text-sm text-semantic-text-secondary">
        No site carries this service type.
      </p>
    );
  }

  const cardRight = layout.cardLeft + layout.cardWidth;
  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-lg border border-semantic-border-primary bg-semantic-bg-secondary transition-[height] duration-500 ease-out motion-reduce:transition-none"
      style={{ height: layout.height }}
    >
      {/* Lines and clouds redraw per scope, once the cards have moved under them. */}
      <svg key={scope} className="absolute inset-0 h-full w-full motion-safe:animate-topology-in-late" aria-hidden>
        <g fill="none">
          {clouds.flatMap((cloud) =>
            cloud.from.map(({ node, health }) => (
              <path
                key={`${cloud.cloud}-${node}`}
                d={cloudPath([cardRight, layout.siteY[node]], [layout.cloudX - 18, layout.cloudY[cloud.cloud]])}
                className={HEALTH_STROKE[health]}
                strokeWidth={2}
                strokeDasharray="6 5"
                strokeLinecap="round"
              />
            ))
          )}
          {layout.arcs.map((arc) => (
            <path
              key={arc.key}
              d={arcPath(layout, arc.top, arc.bottom, arc.depth)}
              className="stroke-semantic-bg-brand-bold"
              strokeWidth={2.5}
              strokeLinecap="round"
            />
          ))}
        </g>
        {clouds.map((cloud) => (
          <circle
            key={cloud.cloud}
            cx={layout.cloudX}
            cy={layout.cloudY[cloud.cloud]}
            r={17}
            className={`${cloudPlateClass(cloud.cloud)} stroke-semantic-bg-brand-bold`}
            strokeWidth={3}
          />
        ))}
      </svg>

      {layout.arcs.map((arc) => (
        <span
          key={`${scope}-${arc.key}`}
          className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full border motion-safe:animate-topology-in-late border-semantic-border-brand-secondary bg-semantic-bg-primary px-1.5 py-px text-[10px] font-bold text-semantic-text-brand shadow-sm"
          style={{ left: arc.caption.x, top: arc.caption.y }}
        >
          {arc.label}
        </span>
      ))}

      {scopedSites.map((site) => {
        const name = nameById.get(site.id) ?? site.id;
        const isSelected = selectedKey === site.id;
        return (
          <SiteCard
            key={site.id}
            site={site}
            name={name}
            health={healthBySite[site.id] ?? "up"}
            isSelected={isSelected}
            onClick={() => onSelectNode(isSelected ? null : { key: site.id, siteIds: [site.id], label: name })}
            style={{
              left: layout.cardLeft,
              top: layout.siteY[site.id] - DIAGRAM.cardHeight / 2,
              width: layout.cardWidth,
              height: DIAGRAM.cardHeight,
            }}
          />
        );
      })}

      {clouds.map((cloud) => (
        <React.Fragment key={`${scope}-${cloud.cloud}`}>
          <CloudMark
            cloud={cloud.cloud}
            className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 motion-safe:animate-topology-in-late"
            style={{ left: layout.cloudX, top: layout.cloudY[cloud.cloud] }}
          />
          <span
            className="pointer-events-none absolute -translate-x-1/2 whitespace-nowrap text-[11px] font-semibold text-semantic-text-brand motion-safe:animate-topology-in-late"
            style={{ left: layout.cloudX, top: layout.cloudY[cloud.cloud] + 22 }}
          >
            {cloud.cloud} · {cloud.services} {cloud.services === 1 ? "service" : "services"}
          </span>
        </React.Fragment>
      ))}

      <TopologyLegend hasLinks={links.length > 0} hasClouds={clouds.length > 0} />
      {/* Why there is no map, said once and quietly. */}
      <p className="absolute bottom-4 right-3 text-[11px] text-semantic-text-secondary">
        Diagram view · no site location on file
      </p>
    </div>
  );
}
